import React, { useEffect, useRef, useState } from 'react';

import AlertCaution from '@rover/icons/svg/alert-caution.svg';
import AlertError from '@rover/icons/svg/alert-error.svg';
import AlertInfo from '@rover/icons/svg/alert-info.svg';
import ReferSocial from '@rover/icons/svg/refer-social.svg';
import { Box } from '@rover/kibble/core';
import Banner from '@rover/kibble/official-patterns/Banner';
import { BannerSeverity } from '@rover/kibble/official-patterns/Banner/BannerSeverity.constants';
import Section from '@rover/kibble/patterns/Section';
import roverTheme from '@rover/kibble/themes/roverTheme';
import useTheme from '@rover/kibble/themes/useTheme';
import { useApiFrontendDjangoMessagesList } from '@rover/rsdk/src/apiClient/latest/api-frontend-django-messages-list';
import { LevelTagEnum } from '@rover/rsdk/src/apiClient/latest/model/levelTagEnum';
import type { SvgIcon } from '@rover/types/src/SvgIcon';

const ALERT_DISMISS_TIMEOUT = 8000;

const getMessageSeverity = (levelTag: LevelTagEnum): BannerSeverity => {
  switch (levelTag) {
    case 'success':
      return BannerSeverity.SUCCESS;
    case 'warning':
      return BannerSeverity.WARNING;
    case 'error':
      return BannerSeverity.DANGER;
    case 'debug':
    case 'info':
    default:
      return BannerSeverity.INFO;
  }
};

const getMessageIcon = (
  levelTag: LevelTagEnum,
  theme: typeof roverTheme
): { icon: SvgIcon; fill: string } => {
  const {
    colors: { border },
  } = theme;
  switch (levelTag) {
    case 'success':
      return { icon: ReferSocial, fill: border.success.toString() };
    case 'warning':
      return { icon: AlertCaution, fill: border.caution.toString() };
    case 'error':
      return { icon: AlertError, fill: border.error.toString() };
    case 'debug':
    case 'info':
    default:
      return { icon: AlertInfo, fill: border.highlight.toString() };
  }
};

const DjangoMessages = (): JSX.Element | null => {
  const theme = useTheme();
  const { data: djangoMessages, isLoading } = useApiFrontendDjangoMessagesList();
  const [showError, setShowError] = useState(true);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    closeTimer.current = setTimeout(() => {
      setShowError(false);
    }, ALERT_DISMISS_TIMEOUT);

    return () => {
      if (closeTimer) {
        clearTimeout(closeTimer.current);
      }
    };
  }, []);

  if (
    !showError ||
    isLoading ||
    !djangoMessages ||
    !djangoMessages.results ||
    djangoMessages.results.length === 0
  ) {
    return null;
  }

  return (
    <Box position="fixed" bottom="0" width="100%" zIndex="1050">
      <Section>
        {djangoMessages.results.map((message, msgsIdx) => {
          const { icon, fill } = getMessageIcon(message.levelTag, theme);
          return (
            <Banner
              key={msgsIdx}
              severity={getMessageSeverity(message.levelTag)}
              icon={icon}
              iconFillColor={fill}
              onClose={(): void => setShowError(false)}
              containerProps={{ mb: '6x' }}
              title={message.message.split('\n').map((line, msgIdx, arr) => (
                <React.Fragment key={msgIdx}>
                  {line}
                  {/* Add <br /> only if it's not the last item */}
                  {msgIdx < arr.length - 1 && <br />}
                </React.Fragment>
              ))}
            />
          );
        })}
      </Section>
    </Box>
  );
};
export default DjangoMessages;
